import { z } from "zod";
import { scheduleApplicationMilestoneBaseSchema } from "@common/schemas/domains/accounting/schedule.schema";
import { isRevenueRecognitionPeriodKey } from "../schemas/domains/accounting/scheduleReview.schema";
import { documentMigrationSourceSchema } from "../schemas/accounting-integration.schema";
import { documentCategorySchema } from "../schemas/documentCategory/document-category.schema";
import {
    IMPORT_REJECTION_ROW_NUMBERS_CAP,
    importRejectionExampleRowSchema,
    importRejectionKindSchema,
    importRejectionRowProblemSchema,
} from "../schemas/domains/accounting/import-rejection.schema";
import { PersistedViewSchema } from "./views/views";
import { TaskType } from "./taskType";
import { MessageSentBySchema, ThreadSchema } from "./chat";
import { ConnectionProvider } from "./integrationConnection";
import {
    DocumentTaxonomyExtractionSchema,
    ExtractionMethod,
    ExtractionStatus,
    FinancialDocumentSchema,
    GlExtractionBasis,
} from "./financialDocument";

const TaskDocumentCategorySchema = documentCategorySchema.omit({
    description: true,
});

const TaskImportRejectionSchema = z.object({
    kind: importRejectionKindSchema,
    summary: z.string().max(100),
    guidance: z.string().max(500),
    documentId: z.number().int().optional(),
    exampleRows: z.array(importRejectionExampleRowSchema).max(3).optional(),
    rowNumbers: z
        .array(z.number().int().min(1))
        .max(IMPORT_REJECTION_ROW_NUMBERS_CAP)
        .optional(),
    rowProblems: z
        .array(importRejectionRowProblemSchema)
        .max(IMPORT_REJECTION_ROW_NUMBERS_CAP)
        .optional(),
    totalCount: z.number().int().min(0).optional(),
});

export const TaskCoaExtractionSchema = z.object({
    status: z.nativeEnum(ExtractionStatus),
    extractionMethod: z.nativeEnum(ExtractionMethod),
    rowsCount: z.number().int().min(0).optional(),
    warnings: z.array(z.string().max(500)).max(50).nullable().optional(),
    rejection: TaskImportRejectionSchema.nullable().optional(),
    isChangedByUser: z.boolean(),
});

export const TaskGlExtractionSchema = z.object({
    status: z.nativeEnum(ExtractionStatus),
    extractionMethod: z.nativeEnum(ExtractionMethod),
    basis: z.nativeEnum(GlExtractionBasis).nullable().optional(),
    rowsCount: z.number().int().min(0).optional(),
    startDate: z.string().date().nullable().optional(),
    endDate: z.string().date().nullable().optional(),
    warnings: z.array(z.string().max(500)).max(50).nullable().optional(),
    rejection: TaskImportRejectionSchema.nullable().optional(),
    isChangedByUser: z.boolean(),
});

export type TaskCoaExtraction = z.infer<typeof TaskCoaExtractionSchema>;
export type TaskGlExtraction = z.infer<typeof TaskGlExtractionSchema>;

export const TaskFinancialDocumentSchema = FinancialDocumentSchema.extend({
    taxonomyExtractionStatus: z.nativeEnum(ExtractionStatus),
    taxonomyExtraction: DocumentTaxonomyExtractionSchema.nullable(),
    documentCategoryId: z.string().nullable().optional(),
    documentCategory: TaskDocumentCategorySchema.nullable().optional(),
    coaExtraction: TaskCoaExtractionSchema.nullable().optional(),
    glExtraction: TaskGlExtractionSchema.nullable().optional(),
});

export type TaskFinancialDocument = z.infer<typeof TaskFinancialDocumentSchema>;

export enum TaskPriority {
    URGENT = "urgent",
    HIGH = "high",
    MEDIUM = "medium",
    LOW = "low",
}

export enum TaskGroupBy {
    STATUS = "status",
    PRIORITY = "priority",
    ASSIGNEE = "assignee",
    DUE_DATE = "due_date",
    TASK_GROUP = "task_group",
    CLOSE_STAGE = "close_stage",
    CLOSE = "close",
    CLIENT = "client",
}

export enum TaskStatus {
    TODO = "todo",
    IN_PROGRESS = "in_progress",
    BLOCKED = "blocked",
    DONE = "done",
    CANCELLED = "cancelled",
}

/**
 * Task statuses visible in the UI. BLOCKED is excluded because it has been
 * removed from the product surface and existing blocked tasks are migrated
 * to TODO.
 */
export const UI_VISIBLE_TASK_STATUSES: readonly TaskStatus[] = [
    TaskStatus.TODO,
    TaskStatus.IN_PROGRESS,
    TaskStatus.DONE,
    TaskStatus.CANCELLED,
];

export const TERMINATING_TASK_STATUSES: readonly TaskStatus[] = [
    TaskStatus.DONE,
    TaskStatus.CANCELLED,
];

export type TerminatingTaskStatus = TaskStatus.DONE | TaskStatus.CANCELLED;

export const isTaskTerminatingStatus = (
    status: TaskStatus | undefined,
): status is TerminatingTaskStatus =>
    status !== undefined && TERMINATING_TASK_STATUSES.includes(status);

export { TaskType };

export {
    MILESTONE_REVENUE_RECOGNITION_REVIEW_TASK_TITLE,
    SCHEDULE_REVIEW_TASK_TYPES,
    SCHEDULE_RULE_SUGGESTED_TASK_TITLE,
    SYSTEM_MANAGED_TASK_TYPES,
    getEntityLockedTooltip,
    isEntityLockedTaskType,
    isScheduleReviewTaskType,
    isSystemManagedTaskType,
} from "../constants/taskTypeDefinitions";
export type {
    ScheduleReviewTaskType,
    SystemManagedTaskType,
} from "../constants/taskTypeDefinitions";

// All task metadata shapes merged. Fields are optional so callers can
// use optional-chaining without explicit type narrowing.
const TaskMetaSchema = z.object({
    // BrokenConnection task
    connectionId: z.string().optional(),
    // TransactionReview task
    reviewFields: z
        .array(
            z.enum([
                "category",
                "counterparty",
                "ledger",
                "accrual_ledger",
                "entity",
                "class",
                "note",
            ]),
        )
        .optional(),
    migrationSource: documentMigrationSourceSchema.optional(),
    migrationImportStatus: z.enum(["importing", "failed"]).optional(),
    migrationImportDocumentId: z.number().optional(),
    migrationImportError: z.string().optional(),
    // Revenue recognition review task: the close month it covers (yyyy-MM)
    periodKey: z.string().optional(),
    // Milestone revenue recognition review task: schedules with a milestone due
    milestoneReview: z
        .object({
            applications: z.array(
                z.object({
                    applicationId: z.string().uuid(),
                    templateId: z.string().uuid(),
                    scheduleName: z.string(),
                    sourceLabel: z.string(),
                    dueMilestones: z.array(
                        scheduleApplicationMilestoneBaseSchema
                            .pick({
                                sortOrder: true,
                                name: true,
                                plannedAmount: true,
                                expectedDate: true,
                            })
                            .extend({ periodId: z.string().uuid() }),
                    ),
                }),
            ),
        })
        .optional(),
    // Integration CoA mapping task: the provider whose transaction types it maps
    integrationProvider: z.nativeEnum(ConnectionProvider).optional(),
});

export type TaskMeta = z.infer<typeof TaskMetaSchema>;

/** System key for the high-value uncategorized bucket (|amount| >= $1,000). */
export const UNCATEGORIZED_SYSTEM_KEY_HIGH = "uncategorized_workflow_high";
/** System key for the low-value uncategorized bucket (|amount| < $1,000). */
export const UNCATEGORIZED_SYSTEM_KEY_LOW = "uncategorized_workflow_low";
/** System key for the P&L activation task. */
export const PROFIT_LOSS_ACTIVATION_SYSTEM_KEY = "profit_loss_activation";
/** System key for the document retrieval task. */
export const DOCUMENT_RETRIEVAL_SYSTEM_KEY = "document_retrieval";

/** System key for the accrual review "review invoice matches" bucket task. */
export const ACCRUAL_REVIEW_INVOICE_SYSTEM_KEY = "accrual_review_invoice";
/** System key for the accrual review "review bill matches" bucket task. */
export const ACCRUAL_REVIEW_BILL_SYSTEM_KEY = "accrual_review_bill";
/** System key for the accrual review "review transfer matches" bucket task. */
export const ACCRUAL_REVIEW_TRANSFER_SYSTEM_KEY = "accrual_review_transfer";
/** System key for the accrual review "review everything else" bucket task. */
export const ACCRUAL_REVIEW_OTHER_SYSTEM_KEY = "accrual_review_other";
/** System key for the accrual review "confirm rule-suggested accrual ledger" bucket task. */
export const ACCRUAL_REVIEW_RULE_SUGGESTED_SYSTEM_KEY =
    "accrual_review_rule_suggested";
/** System key for the per-workspace accrual task group that bundles the accrual review tasks together. */
export const ACCRUAL_TASK_GROUP_SYSTEM_KEY = "accrual";

export const ACCRUAL_REVIEW_SYSTEM_KEYS = [
    ACCRUAL_REVIEW_RULE_SUGGESTED_SYSTEM_KEY,
    ACCRUAL_REVIEW_INVOICE_SYSTEM_KEY,
    ACCRUAL_REVIEW_BILL_SYSTEM_KEY,
    ACCRUAL_REVIEW_TRANSFER_SYSTEM_KEY,
    ACCRUAL_REVIEW_OTHER_SYSTEM_KEY,
] as const;

export const SCHEDULE_TASK_GROUP_SYSTEM_KEY = "schedules";
export const SCHEDULE_RULE_SUGGESTED_REVIEW_SYSTEM_KEY =
    "schedule_rule_suggested_review";
export const SCHEDULE_RULE_SUGGESTED_TASK_DESCRIPTION =
    "Your rules matched these transactions to a schedule but did not " +
    "post automatically. Confirm a suggestion to activate the schedule, or " +
    "dismiss it to leave the transaction as it is.";
export const REVENUE_RECOGNITION_REVIEW_SYSTEM_KEY_PREFIX =
    "revenue_recognition_review";
export const MILESTONE_REVENUE_RECOGNITION_REVIEW_SYSTEM_KEY_PREFIX =
    "milestone_revenue_recognition_review";
export const MILESTONE_REVENUE_RECOGNITION_REVIEW_TASK_DESCRIPTION =
    "These schedules recognize revenue when a milestone is completed, and " +
    "one or more milestones have reached their expected date. Confirm the " +
    "completed milestones and the posting date to recognize them.";

/** One milestone review task per entity, listing every schedule with a due milestone. */
export function milestoneRevenueRecognitionReviewSystemKey(
    entityId: number,
): string {
    return `${MILESTONE_REVENUE_RECOGNITION_REVIEW_SYSTEM_KEY_PREFIX}:${entityId}`;
}

export const INTEGRATION_COA_MAPPING_SYSTEM_KEY_PREFIX =
    "integration_coa_mapping";

/** One integration CoA mapping task per entity per integration provider. */
export function integrationCoaMappingSystemKey(
    provider: ConnectionProvider,
): string {
    return `${INTEGRATION_COA_MAPPING_SYSTEM_KEY_PREFIX}:${provider}`;
}

export function parseRevenueRecognitionReviewPeriodKey(
    systemKey: string | null | undefined,
): string | null {
    const prefix = `${REVENUE_RECOGNITION_REVIEW_SYSTEM_KEY_PREFIX}:`;
    if (!systemKey?.startsWith(prefix)) {
        return null;
    }
    const periodKey = systemKey.slice(prefix.length);
    return isRevenueRecognitionPeriodKey(periodKey) ? periodKey : null;
}

export {
    DocumentRetrievalExclusionReason,
    DOCUMENT_RETRIEVAL_EXCLUSION_LABELS,
} from "./documentRetrieval";

export const TaskSummarySchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
});
export type TaskSummary = z.infer<typeof TaskSummarySchema>;

export const TaskSelectionSummarySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
export type TaskSelectionSummary = z.infer<typeof TaskSelectionSummarySchema>;

const taskSchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().uuid(),
    organizationId: z.string().uuid().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    assigneeId: z.number().nullable(),
    assignee: MessageSentBySchema.nullable().optional(),
    createdById: z.number().nullable(),
    createdBy: MessageSentBySchema.nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    status: z.nativeEnum(TaskStatus),
    type: z.nativeEnum(TaskType),
    priority: z.nativeEnum(TaskPriority).nullable(),
    dueDate: z.string().nullable(),
    estimatedTime: z.number().nullable().optional(),
    closeId: z.string().uuid().nullable().optional(),
    closeStageId: z.string().uuid().nullable().optional(),
    messageId: z.string().uuid().nullable().optional(),
    groupId: z.string().uuid().nullable().optional(),
    entityIds: z.array(z.coerce.number()).nullable().optional(),
    thread: ThreadSchema.nullable().optional(),
    view: PersistedViewSchema.nullable().optional(),
    financialDocuments: z.array(TaskFinancialDocumentSchema).optional(),
    /**
     * Optimistic-update field. The backend never populates this; the
     * frontend writes it during `useUpdateTask`'s `onMutate` so consumers
     * have an authoritative ID set before the server response (which only
     * carries the rehydrated `financialDocuments` array) lands.
     */
    financialDocumentIds: z.array(z.number()).optional(),
    blockingTaskIds: z.array(z.string().uuid()).optional(),
    blockedByTaskIds: z.array(z.string().uuid()).optional(),
    numberOfActiveBlockedByTasks: z.number().int().min(0).optional(),
    /** Set on listed tasks when exactly one open task blocks this one. */
    soleActiveBlockedByTask: TaskSummarySchema.optional(),
    relatedTaskIds: z.array(z.string().uuid()).optional(),
    metadata: TaskMetaSchema.nullable().optional(),
    systemKey: z.string().nullable().optional(),
    lastDoneAt: z.coerce.date().nullable().optional(),
    lastDoneById: z.number().nullable().optional(),
    lastDoneBy: MessageSentBySchema.nullable().optional(),
});

export type Task = z.infer<typeof taskSchema>;
export const TaskSchema: z.ZodType<Task, z.ZodTypeDef, unknown> = taskSchema;
